/**
*
* FavoritesPage
*
*/
import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import helpContext from 'constants/helpContext';
import Sidebar from 'Workspace/components/WorkspaceSidebar';
import WorkspaceHeader from 'Workspace/components/WorkspaceHeader';
import HomeNavigation from 'Workspace/components/HomeNavigation';
import ProjectActivityList from 'Workspace/Projects/components/ProjectActivityList';
import HelpMenu from 'Workspace/components/WorkspaceHelpMenu';
import { selectCurrentMe } from 'data/CurrentUser/selectors';
import { selectWorkspacesList } from 'Workspace/Admin/store/selectors';
import enhanceWithServices from 'hocs/enhanceWithServices';
import gtmService from 'utils/gtm/gtmService';
import { GTM_EVENT_TYPES } from 'utils/gtm/constants';
import {
  PageWrapper,
  PageContent,
} from 'components/PageStyles';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import {
  EmptyContentWrapper,
  MessageParagraph,
  EmptyStateWrapper,
  EmptyStateImageWrapper,
  ErrorStateImageWrapper,
  ErrorContentWrapper,
  StyledPageViewport,
} from './styles';
import messages from './messages';

const FavoritesPage = ({currentUser, workspaces, projectService, favoritesService, activityService, annotationService}) => {
  const [activityList, setActivityList] = useState([]);
  const [sortedActivityList, setSortedActivityList] = useState();
  const [sortOrder, setSortOrder] = useState('asc');
  const [loading , setLoading] = useState(true);
  
  useEffect(()=>{
    gtmService.gtmTrackEvent({ eventType: GTM_EVENT_TYPES.PAGEVIEW, payload: { pageName: 'Favorites Page' } });
  }, []);

  useEffect(() => {
    async function getUserFavouriteList() {
      const accountId = currentUser?.accountId;
      return await favoritesService.getFavouriteActivityList(accountId);
    }

    async function fetchUserFavorites() {
      const favouriteActivityResponse = await getUserFavouriteList();
      const favoritesActivityList = favouriteActivityResponse?.favorites;
      const activityList = [];

      if(!favoritesActivityList || 
        (favoritesActivityList && favoritesActivityList.length === 0)) {
        setLoading(false);
        setActivityList(favoritesActivityList);
        return;
      }
      
      favoritesActivityList.forEach( (activity) => {
        const activityId = activity.activityId || activity.rootElementId;
        activityService.getActivity(activityId).then((activityResponse) => {
          const { config: { title } } = activityResponse;
          activityResponse.configFields = {};
          activityResponse.configFields.title = title;
          activityResponse.rootElementId = activity.rootElementId;
          activityResponse.favoriteId = activity.id;
          projectService.getProject(activity.projectId).then( (projectDetail) => {
            activityResponse.projectName = projectDetail.name;
            activityResponse.isFavourite = true;
            workspaces && workspaces.forEach((workspace) => {
              if(workspace.id === activity.workspaceId) {
                activityResponse.workspaceName = workspace.name;
              }
            });
            activityList.push(activityResponse);
            if(activityList.length === favoritesActivityList.length) {
              setLoading(false);
              setActivityList(activityList);
            }
          });
        });
      });
    }

    if(currentUser && workspaces) {
      fetchUserFavorites();
    }
  }, [currentUser, projectService, favoritesService, activityService, workspaces]);

  useEffect(() => {
    if(activityList && activityList.length > 0) {
      const activityListUpdated = sortedActivityListUtil(activityList, sortOrder);
      setSortedActivityList([...activityListUpdated]);
    } else if (activityList && activityList.length === 0) {
      setSortedActivityList([]);
    } else {
      setSortedActivityList(null);
    }
  }, [sortOrder, activityList, sortedActivityListUtil]);
  
  const orderByType = (data = '') => (new Date(data.updatedAt));
  
  const sortedActivityListUtil = useCallback((activityList, order) => activityList.sort( 
    (a, b) => {
      if(order === 'desc') {
        return orderByType(a) - orderByType(b);
      } else 
        return orderByType(b) - orderByType(a);
    }), 
  []);

  const sortFavourites = () => {
    const updatedSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    const activityListUpdated = sortedActivityListUtil(sortedActivityList, updatedSortOrder);
    const arrayNew = [ ...activityListUpdated];
    setSortedActivityList(arrayNew);
    setSortOrder(updatedSortOrder);
  };

  const handleRemoveFavorite = async (activity) => {
    const { accountId } = currentUser;
    const { favoriteId, activityId, rootElementId } = activity;
    const response = await favoritesService.removeFavorites(accountId, favoriteId, rootElementId);
    if(response) {
      const activityIndex = activityList.findIndex((activity) => (activity.activityId === activityId));
      activityList.splice(activityIndex, 1);
      setActivityList([...activityList]);
    }
  };

  const favoritesEmptyParagarph = () => (
    <><MessageParagraph>{intl.formatMessage(messages.favoriteEmptyMessage1)}</MessageParagraph>
    <MessageParagraph>{intl.formatMessage(messages.favoriteEmptyMessage2)}</MessageParagraph>
    <MessageParagraph>{intl.formatMessage(messages.favoriteEmptyMessage3)}</MessageParagraph>
    </>
  );

  const favoritesErrorParagarph = () => (
    <><MessageParagraph>{intl.formatMessage(messages.favoriteErrorMessage1)}</MessageParagraph>
    <MessageParagraph>{intl.formatMessage(messages.favoriteErrorMessage2)}</MessageParagraph>
    </>
  );
  
  const favoritesEmptyErrorContainer = (isEmpty) => (
    <EmptyStateWrapper>
      {isEmpty ? 
      <><EmptyStateImageWrapper /><EmptyContentWrapper>
        {favoritesEmptyParagarph()}
      </EmptyContentWrapper></> : 
      <><ErrorStateImageWrapper /><ErrorContentWrapper>
        {favoritesErrorParagarph()}
      </ErrorContentWrapper></>}
    </EmptyStateWrapper>
  );

  const favoritesListContainer = () => (
    <PageContent>
      {sortedActivityList && sortedActivityList.length === 0 && favoritesEmptyErrorContainer(true)}
      {!sortedActivityList && favoritesEmptyErrorContainer(false)}
      {sortedActivityList && sortedActivityList.length > 0 &&
        <ProjectActivityList
          activityList={sortedActivityList}
          currentUser={currentUser}
          toggleFavourite={handleRemoveFavorite}
          sortActivityListByDate={sortFavourites}
          annotationService={annotationService}
          sortOrder={sortOrder}
          isWorkspaceRedesignPage={true}
        />}
    </PageContent>
  );

  return (
    <PageWrapper>
      <Helmet>
        <title>{intl.formatMessage(messages.pageTitle)}</title>
        <meta
          name="description"
          content={intl.formatMessage(messages.pageDescription)}
        />
      </Helmet>
      <Sidebar navigation={<HomeNavigation showHeader selectedKey="favorites"/>} ></Sidebar>
      <StyledPageViewport>
        <WorkspaceHeader title={intl.formatMessage(messages.favorites)} workspaceRedesign={true}/>
        {!loading && favoritesListContainer()}
      </StyledPageViewport>
      <HelpMenu helpContext={helpContext.HOME}/>
    </PageWrapper>
  );
};

const mapStateToPros = (state) => ({
  currentUser: selectCurrentMe(state),
  workspaces: selectWorkspacesList(state),
});

export default connect(mapStateToPros)(enhanceWithServices(FavoritesPage));
